import React, { useState } from 'react';
import { Alert, ActionIcon, Box } from '@mantine/core';
import { IconX, IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useEmailWarning } from '../../../hooks/useEmailWarning';
import { EmailProblemType } from '../../../entities/EmailProblem/EmailProblem';

const EmailWarningSticky: React.FC = () => {
    const { t } = useTranslation();
    const { shouldShowWarning, emailProblemType, isLoading } = useEmailWarning();
    const [dismissed, setDismissed] = useState(false);

    // Don't show if loading, no warning needed, or user dismissed
    if (isLoading || !shouldShowWarning || dismissed) 
        return null;

    const handleDismiss = () => {
        setDismissed(true);
    };

    const getAlertStyles = () => {
        switch (emailProblemType) {
            case EmailProblemType.Complaint:
                return {
                    color: 'red',
                    backgroundColor: '#fee2e2', // Light red background
                    borderColor: '#dc2626', // Vivid red border
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
            case EmailProblemType.HardBounce:
                return {
                    color: 'red',
                    backgroundColor: '#fef3c7', // Light yellow background
                    borderColor: '#f59e0b', // Orange border
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
            default:
                return {
                    color: 'yellow',
                    backgroundColor: '#fef3c7',
                    borderColor: '#f59e0b',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                };
        }
    };

    return (
        <Box
            style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                padding: '16px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(4px)',
            }}
        >
            <Alert
                color={getAlertStyles().color}
                icon={<IconAlertTriangle size={20} />}
                withCloseButton={false}
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    boxShadow: '0 8px 25px rgba(220, 38, 38, 0.3)',
                    backgroundColor: getAlertStyles().backgroundColor,
                    borderColor: getAlertStyles().borderColor,
                    borderWidth: getAlertStyles().borderWidth,
                    borderStyle: getAlertStyles().borderStyle,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <strong style={{ fontSize: '16px', color: '#dc2626' }}>{t('emailWarning.title')}</strong>
                        <br />
                        <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                            {t('emailWarning.message')}
                        </span>
                        <br />
                        <small style={{ color: '#6b7280', fontWeight: '500' }}>{t('emailWarning.contactSupport')}</small>
                    </div>
                    <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={handleDismiss}
                        style={{ 
                            marginLeft: '16px', 
                            flexShrink: 0,
                            backgroundColor: 'rgba(220, 38, 38, 0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(220, 38, 38, 0.2)'
                            }
                        }}
                        aria-label={t('emailWarning.dismiss')}
                    >
                        <IconX size={18} />
                    </ActionIcon>
                </div>
            </Alert>
        </Box>
    );
};

export default EmailWarningSticky; 